import React, {useEffect, useState} from "react";
import ProductHeader from "./ProductHeader";

const views = [
  ["entry", "Entry decisions"], ["exit", "Exit evidence"], ["replay", "Entry replay and labels"],
  ["learning", "Training examples"], ["training", "Daily training reviews"],
];
const label = value => String(value ?? "").replaceAll("_", " ");
const today = () => new Intl.DateTimeFormat("en-CA", {timeZone:"Asia/Kolkata", year:"numeric", month:"2-digit", day:"2-digit"}).format(new Date());

function describe(row) {
  const data = row.data;
  if (data.candidate) {
    const candidate = data.candidate;
    return [candidate.direction, label(candidate.pattern_type), `Trigger ${candidate.proposed_entry_index}`,
      `Stop ${candidate.proposed_stop_index}`, label(data.outcome?.reason)].filter(Boolean).join(" · ");
  }
  return [data.contract, label(data.task), label(data.status), label(data.pattern),
    data.entry != null ? `Live entry ₹${data.entry}` : "",
    data.bid != null ? `Paper exit ₹${data.bid}` : "",
    data.target != null ? `Target ${data.target}` : "",
    label(data.reason || data.reason_codes?.join(" · "))].filter(Boolean).join(" · ");
}

export default function AILearningLibrary() {
  const [day, setDay] = useState(today);
  const [symbol, setSymbol] = useState("NIFTY");
  const [view, setView] = useState("entry");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(()=>{
    if (!day) return;
    const controller = new AbortController();
    const params = new URLSearchParams({day, symbol, view});
    fetch(`/api/ai-learning-library?${params}`, {signal:controller.signal, cache:"no-store"})
      .then(async response=>{
        const body = await response.json();
        if (!response.ok) throw Error(body.detail || "Learning Library unavailable");
        if (!controller.signal.aborted) setResult(body);
      })
      .catch(err=>{if (!controller.signal.aborted) setError(err.message);});
    return ()=>controller.abort();
  }, [day, symbol, view, refresh]);

  function update(setter, value) {
    setResult(null);
    setError("");
    setter(value);
    setRefresh(current=>current + 1);
  }

  return <section className="panel exit-assistant ai-learning-library">
    <ProductHeader title="AI Learning Library" mode="paper" paperOnly status="Read-only decision and training evidence"/>
    <div className="panel-body">
      <div className="exit-assistant-toolbar library-filters">
        <label>Trading date (IST) <input type="date" value={day} onChange={event=>update(setDay,event.target.value)}/></label>
        <label>Index <select value={symbol} onChange={event=>update(setSymbol,event.target.value)}><option>NIFTY</option><option>SENSEX</option></select></label>
        <label>Evidence <select value={view} onChange={event=>update(setView,event.target.value)}>
          {views.map(([key,name])=><option key={key} value={key}>{name}</option>)}
        </select></label>
        <button onClick={()=>update(setRefresh,refresh)}>Refresh</button>
      </div>
      {view==="replay" && <p>Replay labels describe historical index movement. Ambiguous, invalid and unfilled entries are excluded from binary training. Option execution and P&amp;L require separate validation.</p>}
      {error ? <p role="alert">{error}</p> : !day ? <p>Select a trading date.</p> : !result ? <p role="status">Loading evidence…</p> : <>
        <p>{Object.entries(result.counts).map(([name,count])=>`${label(name)} ${count}`).join(" · ")}</p>
        {!result.rows.length && <p>{view==="replay" && !result.manifest ? "No replay bundle has been published for this index." : "No matching records for this trading date."}</p>}
        {result.manifest && <details><summary>Replay input and validation metadata</summary><pre>{JSON.stringify(result.manifest,null,2)}</pre></details>}
        {result.rows.length>0 && <div className="positions-table-scroll">
          <table className="exit-assistant-table mobile-record-table" role="table"><caption>Latest {result.rows.length} matching records</caption>
            <thead><tr role="row"><th role="columnheader" scope="col">Recorded at (IST)</th><th role="columnheader" scope="col">Event</th><th role="columnheader" scope="col">Evidence</th></tr></thead>
            <tbody>{result.rows.map((row,index)=><tr role="row" key={`${row.recorded_at}-${index}`}>
              <td role="cell" data-label="Recorded at (IST)">{row.recorded_at && new Date(row.recorded_at).toLocaleString("en-IN",{timeZone:"Asia/Kolkata"})}</td>
              <td role="cell" data-label="Event">{label(row.event)}</td><td role="cell" data-label="Evidence"><details><summary>{describe(row) || "View record"}</summary>
                <pre style={{whiteSpace:"pre-wrap",overflowWrap:"anywhere"}}>{JSON.stringify(row.data,null,2)}</pre>
              </details></td>
            </tr>)}</tbody>
          </table>
        </div>}
      </>}
    </div>
  </section>;
}
